import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { LabelS } from '#src/atoms/typography/index.tsx';
import { range } from '#src/lib/arrays.ts';
import type { Color, LinearGradient } from '#src/style/types.ts';

const Root = styled(Horizontal)`
  --active-color: var(--c-black);
  --last-active-color: var(--c-black);
  --color: var(--c-nd-600);
  --nb-items: 3;

  flex: 1;
  & > :first-child {
    flex: 1;
  }
`;

const ProgressWrapper = styled.div`
  width: calc(100% / var(--nb-items));
  height: var(--unit);
  border-radius: 2px;
  background: var(--color);
  &.filled {
    background: var(--active-color);
    &.lastActive {
      background: var(--last-active-color);
    }
  }
  &.highlightNext {
    background: var(--highlight-next-color);
  }
`;

type Props = {
  progress: number | null;
  target: number | null;
  color?: Color | LinearGradient;
  activeColor?: Color | LinearGradient;
  lastActiveColor?: Color | LinearGradient;
  highlightNextColor?: Color | LinearGradient;
  className?: string;
  showLabel?: boolean;
};
export const Progress = ({
  progress,
  target,
  color,
  activeColor,
  lastActiveColor,
  highlightNextColor,
  className,
  showLabel = true,
}: Props) => {
  if (!target) {
    return null;
  }

  return (
    <Root
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={target}
      aria-valuenow={progress || 0}
      className={className}
      gap={1}
      style={
        {
          '--color': color,
          '--active-color': activeColor,
          '--last-active-color': lastActiveColor,
          '--highlight-next-color': highlightNextColor,
          '--nb-items': target,
        } as CSSProperties
      }
    >
      <Horizontal gap={0.5}>
        {range(target).map(x => (
          <ProgressWrapper
            key={x}
            className={classnames({
              filled: x < (progress || 0),
              lastActive: x === progress,
              highlightNext: highlightNextColor && x === (progress || 0),
            })}
          />
        ))}
      </Horizontal>
      {!!showLabel && (
        <LabelS>
          <Horizontal gap={0.5}>
            <FormattedMessage
              defaultMessage="{progress}/{target}"
              values={{
                progress: (
                  <LabelS
                    as="span"
                    color={highlightNextColor || activeColor}
                    bold
                  >
                    {progress}
                  </LabelS>
                ),
                target: <LabelS as="span">{target}</LabelS>,
              }}
            />
          </Horizontal>
        </LabelS>
      )}
    </Root>
  );
};
